import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";

export const Route = createFileRoute("/_authed/commands/")({ component: CommandHistory });

type Outcome = components["schemas"]["CommandRequestPublic"]["outcome"];
type Capability = components["schemas"]["CapabilityCount"]["capability"];
const outcomes: Record<Outcome, string> = {
  unsupported: "Missing capability", clarify: "Asked a follow-up question", ready: "Awaiting confirmation",
  executed: "Completed", cancelled: "Cancelled", expired: "Confirmation expired",
  blocked: "Blocked by setup or access", error: "Technical error", processing: "Processing",
  interrupted: "Interrupted before a result was saved",
  handoff: "Continue in the app",
};
const capabilities: Record<Capability, string> = {
  recurring_tasks: "Recurring tasks", assignment: "Assigning someone", multiple_tasks: "Multiple tasks",
  dog_subset: "Selected groups of dogs", edit_task: "Editing tasks", delete_task: "Deleting tasks",
  complete_task: "Checking off tasks", messaging: "Messages", purchases: "Purchases",
  medication: "Medication", other: "Other capabilities",
};
const selectClass = "rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm";

function CommandHistory() {
  const [outcome, setOutcome] = useState<Outcome | "">("");
  const [capability, setCapability] = useState<Capability | "">("");
  const [days, setDays] = useState(30);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const query = useQuery({
    queryKey: ["admin", "command-requests", outcome, capability, days, cursor],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/command-requests", {
        params: { query: { outcome: outcome || undefined, capability: capability || undefined,
          days, cursor, limit: 30 } },
      });
      if (error || !data) throw new Error("Could not load request history.");
      return data;
    },
  });
  return <div className="space-y-5">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-lg font-semibold">Talk to Toodles requests</h1>
        <p className="text-sm text-zinc-500">Review current action drafts and earlier request history.</p>
      </div>
      <Button variant="secondary" disabled={query.isFetching} onClick={() => void query.refetch()}>Refresh</Button>
    </div>
    <div className="flex flex-wrap gap-3">
      <label className="flex flex-col gap-1 text-sm">Outcome
        <select className={selectClass} value={outcome} onChange={e => { setOutcome(e.target.value as Outcome | ""); setCursors([undefined]); }}>
          <option value="">All outcomes</option>
          {Object.entries(outcomes).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm">Capability
        <select className={selectClass} value={capability} onChange={e => { setCapability(e.target.value as Capability | ""); setCursors([undefined]); }}>
          <option value="">All capabilities</option>
          {Object.entries(capabilities).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm">Time period
        <select className={selectClass} value={days} onChange={e => { setDays(Number(e.target.value)); setCursors([undefined]); }}>
          {[7, 30, 90, 365].map(n => <option key={n} value={n}>Last {n} days</option>)}
        </select>
      </label>
    </div>
    {query.isError ? <p role="alert" className="text-red-700">Could not load request history. Try Refresh.</p> : query.isPending ?
      <p>Loading requests…</p> : <>
        <section className="space-y-2" aria-label="Missing capabilities">
          <h2 className="font-semibold">Earlier requests with missing capabilities</h2>
          <p className="text-sm text-zinc-500">These categories belong to earlier request tracking. Counts are attempts, not unique people or conversations. Current operation drafts keep their exact action and outcome below.</p>
          <div className="flex flex-wrap gap-2">
            {query.data.capability_counts.map(item => <Button key={item.capability} variant="secondary" onClick={() => {
              setCapability(item.capability); setOutcome("unsupported"); setCursors([undefined]);
            }}>{capabilities[item.capability]} · {item.count}</Button>)}
            {!query.data.capability_counts.length ? <p className="text-sm text-zinc-500">No capability gaps recorded in this period.</p> : null}
          </div>
        </section>
        <p className="text-sm text-zinc-500">Drafts show their latest state, including actions selected using controls. A follow-up question does not mean the request was abandoned. Continuing in the app does not prove an interactive payment, media or security step completed.</p>
        {!query.data.items.length ? <Card><CardBody>No requests match these filters.</CardBody></Card> : null}
        {query.data.items.map(item => <Card key={item.id}><CardBody>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-sm text-zinc-500">
              <span className="font-medium text-zinc-900">{outcomes[item.outcome]}</span>
              <span>{item.user_name ?? "Member"}</span>
              <time dateTime={item.created_at}>{new Date(item.created_at).toLocaleString()}</time>
              <span>{item.source === "command_draft" ? "Current action draft" : "Earlier request"}</span>
            </div>
            {item.operation ? <p className="text-sm text-zinc-600">Action: {item.operation} · v{item.operation_version} · revision {item.revision}</p> : null}
            {item.missing_capabilities.length ? <p className="text-sm text-zinc-600">{item.missing_capabilities.map(c => capabilities[c]).join(", ")}</p> : null}
            <div><h3 className="text-sm font-semibold">Request</h3><p className="whitespace-pre-wrap break-words">{item.message}</p></div>
            <div><h3 className="text-sm font-semibold">Toodles’ response</h3><p className="whitespace-pre-wrap break-words">{item.response ?? "No response was saved."}</p></div>
            {item.error_code ? <p className="text-sm text-red-700">Error: {item.error_code}</p> : null}
            {item.history.length ? <details className="text-sm"><summary className="cursor-pointer">Earlier messages in this request</summary>
              <div className="mt-2 space-y-2">{item.history.map((turn, index) => <p className="whitespace-pre-wrap break-words" key={index}>
                <strong>{turn.role === "user" ? "Member" : "Toodles"}: </strong>{turn.text}
              </p>)}</div>
            </details> : null}
          </div>
        </CardBody></Card>)}
        <div className="flex items-center gap-3">
          <Button variant="secondary" disabled={cursors.length === 1} onClick={() => setCursors(stack => stack.slice(0, -1))}>Previous</Button>
          <span className="text-sm">Page {cursors.length}</span>
          <Button variant="secondary" disabled={!query.data.next_cursor} onClick={() => setCursors(stack => [...stack, query.data.next_cursor ?? undefined])}>Next</Button>
        </div>
      </>}
  </div>;
}
